import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { logout } from '../api';
import { signupInterestLabel } from '../lib/signupInterest';

const BASE = window.location.hostname === 'localhost' ? 'https://schvalovanie.mgtech.sk' : '';

interface Tenant {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  created_at: string;
  active: boolean;
  signup_status?: string | null;
  signup_source?: { zaujem?: string } | null;
  billing_plan: string;
  has_slack_bot_token: boolean;
  imap_host: string | null;
  accounting_email: string | null;
  features?: Record<string, any> | null;
  has_flowii_api_key?: boolean;
  has_flowii_password?: boolean;
  has_omega?: boolean;
  has_telegram?: boolean;
}

interface UsageRow {
  slug: string; name: string; ico: string | null; billing_model: string;
  price_per_document: string | null; month: string; documents: number; imported: number;
}

// Počítadlo dokladov: riadok = fakturovaná firma (rovnaké IČO = jedna firma,
// aj keď má v appke viac okruhov), stĺpce = posledné mesiace + spolu.
function UsagePanel() {
  const [rows, setRows] = useState<UsageRow[] | null>(null);
  useEffect(() => {
    api.get('/auth/billing/usage').then(r => setRows(r.data || [])).catch(() => setRows([]));
  }, []);
  if (!rows || rows.length === 0) return null;
  const months = Array.from(new Set(rows.map(r => r.month))).sort().reverse().slice(0, 6);
  const groups = new Map<string, { names: Set<string>; slugs: Set<string>; byMonth: Record<string, { docs: number; imp: number; eur: number }>; total: { docs: number; imp: number; eur: number }; priced: boolean }>();
  for (const r of rows) {
    const key = r.ico || r.slug;
    if (!groups.has(key)) groups.set(key, { names: new Set(), slugs: new Set(), byMonth: {}, total: { docs: 0, imp: 0, eur: 0 }, priced: false });
    const g = groups.get(key)!;
    const price = r.billing_model === 'per_document' ? (parseFloat(r.price_per_document || '') || 0) : 0;
    g.names.add(r.name); g.slugs.add(r.slug);
    if (price) g.priced = true;
    const n = r.documents + (r.imported || 0);
    const m = g.byMonth[r.month] || (g.byMonth[r.month] = { docs: 0, imp: 0, eur: 0 });
    m.docs += n; m.imp += r.imported || 0; m.eur += n * price;
    g.total.docs += n; g.total.imp += r.imported || 0; g.total.eur += n * price;
  }
  const list = Array.from(groups.values()).sort((a, b) => b.total.docs - a.total.docs);
  const eur = (v: number) => `${v.toLocaleString('sk-SK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  const cell = (c: { docs: number; imp: number; eur: number } | undefined, priced: boolean) => !c ? <span style={{ color: '#d1d5db' }}>—</span> : (
    <>
      {c.docs}
      {c.imp > 0 && <div style={{ color: '#9ca3af', fontSize: '0.7rem' }}>z toho import {c.imp}</div>}
      {priced && <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>{eur(c.eur)}</div>}
    </>
  );
  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto', marginTop: '1.5rem' }}>
      <div style={{ padding: '1rem 1rem 0.25rem' }}>
        <h3 style={{ margin: 0, color: '#1f2937' }}>Počítadlo dokladov</h3>
        <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.8rem' }}>
          Doklady cez OCR po mesiacoch: prijaté (aj zamietnuté) + import vystavených faktúr z PDF, zmazané nie. Firmy s rovnakým IČO sú spolu. Suma len pri modeli Per-doklad s cenou, bez DPH.
        </p>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f9fafb' }}>
            <th style={th}>Firma</th>
            {months.map(m => <th key={m} style={{ ...th, textAlign: 'right' }}>{m}</th>)}
            <th style={{ ...th, textAlign: 'right' }}>Spolu</th>
          </tr>
        </thead>
        <tbody>
          {list.map(g => (
            <tr key={Array.from(g.slugs).join(',')} style={{ borderTop: '1px solid #e5e7eb' }}>
              <td style={td}>
                <div style={{ fontWeight: 500 }}>{Array.from(g.names).join(' / ')}</div>
                {g.slugs.size > 1 && <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{Array.from(g.slugs).join(' + ')}</div>}
              </td>
              {months.map(m => <td key={m} style={{ ...td, textAlign: 'right' }}>{cell(g.byMonth[m], g.priced)}</td>)}
              <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{cell(g.total, g.priced)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SuperAdmin() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [newTenant, setNewTenant] = useState({
    companyName: '', companySlug: '', adminEmail: '', adminFirstName: '', adminLastName: '', adminPassword: ''
  });
  const [creating, setCreating] = useState(false);
  const [createMessage, setCreateMessage] = useState('');
  const [lookupIco, setLookupIco] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [pendingRuzData, setPendingRuzData] = useState<any>(null);  // detaily z RÚZ, aplikujeme PUT po create
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[] | null>(null);
  const [searching, setSearching] = useState(false);
  const navigate = useNavigate();

  const lookupFromRUZ = async () => {
    if (!lookupIco || lookupIco.length < 6) {
      setCreateMessage('✗ Zadaj IČO (6-8 číslic)');
      return;
    }
    setLookingUp(true);
    setCreateMessage('');
    try {
      const res = await api.get(`/auth/company-lookup/${lookupIco}`);
      // Predvyplň názov firmy + slug
      setNewTenant(prev => ({
        ...prev,
        companyName: prev.companyName || res.data.name || '',
        companySlug: prev.companySlug || (res.data.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30),
      }));
      // Ulož aj ostatné detaily — aplikujú sa cez PUT hneď po úspešnom create
      setPendingRuzData({
        ico: lookupIco,
        dic: res.data.dic || null,
        ic_dph: res.data.ic_dph || null,
        address_street: res.data.address_street || null,
        address_city: res.data.address_city || null,
        address_zip: res.data.address_zip || null,
        address_country: res.data.address_country || 'SK',
      });
      const src = res.data.source || 'RPO/RÚZ';
      const vat = res.data.ic_dph_valid === true ? ', IČ DPH overené VIES'
        : res.data.ic_dph_valid === false ? ', ⚠ IČ DPH NEoverené' : '';
      setCreateMessage(`✓ Načítané z ${src}${vat} (uložia sa po vytvorení)`);
    } catch (err: any) {
      setCreateMessage('✗ ' + (err.response?.data?.error || 'Chyba pri načítaní'));
    } finally {
      setLookingUp(false);
    }
  };

  // Vyhľadanie firmy podľa NÁZVU (alebo IČO) cez RPO
  const searchCompany = async () => {
    const q = searchQuery.trim();
    if (q.length < 3) {
      setCreateMessage('✗ Zadaj aspoň 3 znaky (názov firmy alebo IČO)');
      return;
    }
    setSearching(true);
    setSearchResults(null);
    setCreateMessage('');
    try {
      const res = await api.get(`/auth/company-search`, { params: { q } });
      setSearchResults(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setCreateMessage('✗ ' + (err.response?.data?.error || 'Chyba pri vyhľadávaní'));
    } finally {
      setSearching(false);
    }
  };

  // Výber kandidáta → predvyplň názov/slug + ulož RPO adresu;
  // dotiahni DIČ/IČ DPH cez agregát (RÚZ + VIES).
  const pickCandidate = async (c: any) => {
    setSearchResults(null);
    setSearchQuery('');
    setLookupIco(c.ico || '');
    setNewTenant(prev => ({
      ...prev,
      companyName: c.name || prev.companyName,
      companySlug: prev.companySlug || (c.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30),
    }));
    setPendingRuzData({
      ico: c.ico || null,
      dic: null,
      ic_dph: null,
      address_street: c.address_street || null,
      address_city: c.address_city || null,
      address_zip: c.address_zip || null,
      address_country: c.address_country || 'SK',
    });
    if (!c.ico) return;
    setLookingUp(true);
    setCreateMessage('');
    try {
      const res = await api.get(`/auth/company-lookup/${c.ico}`);
      const d = res.data;
      setPendingRuzData((p: any) => ({
        ...(p || {}),
        ico: c.ico,
        dic: d.dic || null,
        ic_dph: d.ic_dph || null,
        address_street: d.address_street || p?.address_street || null,
        address_city: d.address_city || p?.address_city || null,
        address_zip: d.address_zip || p?.address_zip || null,
        address_country: d.address_country || p?.address_country || 'SK',
      }));
      const vat = d.ic_dph_valid === true ? 'IČ DPH overené VIES'
        : d.ic_dph_valid === false ? '⚠ IČ DPH NEoverené' : 'DIČ doplnené';
      setCreateMessage(`✓ ${c.name} (RPO + ${vat}) — uloží sa po vytvorení`);
    } catch (err: any) {
      setCreateMessage('✓ Názov a adresa z RPO (DIČ sa nepodarilo) — uloží sa po vytvorení');
    } finally {
      setLookingUp(false);
    }
  };

  useEffect(() => {
    loadTenants();
  }, [includeInactive]);

  const loadTenants = async () => {
    try {
      const response = await api.get(`/auth/tenants${includeInactive ? '?include_inactive=true' : ''}`);
      setTenants(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Chyba pri načítaní firiem');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setCreateMessage('');
    try {
      await api.post('/auth/create-tenant', newTenant);

      // Ak sme pre lookup RÚZ dáta — aplikuj ich cez PUT na novovytvorenú firmu
      if (pendingRuzData) {
        try {
          await api.put(`/auth/tenant/${newTenant.companySlug}`, pendingRuzData);
        } catch (putErr) {
          console.error('Aktualizácia RÚZ údajov zlyhala:', putErr);
        }
      }

      setCreateMessage('✓ Firma vytvorená');
      setNewTenant({ companyName: '', companySlug: '', adminEmail: '', adminFirstName: '', adminLastName: '', adminPassword: '' });
      setLookupIco('');
      setPendingRuzData(null);
      setTimeout(() => { setShowCreate(false); setCreateMessage(''); }, 1200);
      loadTenants();
    } catch (err: any) {
      setCreateMessage('✗ ' + (err.response?.data?.error || 'Chyba'));
    } finally {
      setCreating(false);
    }
  };

  // Lifecycle akcie (deaktivovať / obnoviť / vymazať natrvalo) sú zámerne
  // LEN v editácii firmy → tab "Pokročilé" (DangerTab v TenantEdit), nie
  // v tomto zozname — aby sa nedali spustiť omylom z prehľadu.

  // Aktivácia self-registered (pending) firmy: schváli ju + pošle adminovi
  // e‑mail s odkazom na nastavenie hesla.
  const handleApprove = async (t: Tenant) => {
    if (!window.confirm(`Aktivovať firmu „${t.name}"? Adminovi sa pošle e‑mail s odkazom na nastavenie hesla.`)) return;
    try {
      const res = await api.post(`/auth/tenant/${t.slug}/approve`);
      window.alert(res.data?.message || 'Firma aktivovaná.');
      loadTenants();
    } catch (err: any) {
      window.alert('✗ ' + (err.response?.data?.error || 'Aktiváciu sa nepodarilo dokončiť.'));
    }
  };

  const handleLogout = () => logout();

  if (loading) return <div style={{ padding: '2rem' }}>Načítavam...</div>;

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f3f4f6' }}>
      {/* Header */}
      <div style={{ backgroundColor: 'white', padding: '1rem 2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 50 }}>
        <h1 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem' }}>Super Admin — Správa firiem</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => navigate('/home')}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Moduly
          </button>
          <button onClick={handleLogout}
            style={{ padding: '0.5rem 1rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
            Odhlásiť
          </button>
        </div>
      </div>

      <div style={{ padding: '2rem' }}>
        {/* Akcie nad tabuľkou */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#374151', fontSize: '0.875rem' }}>
            <input type="checkbox" checked={includeInactive} onChange={e => setIncludeInactive(e.target.checked)} />
            Zobraziť aj deaktivované
          </label>
          <button onClick={() => setShowCreate(true)}
            style={{ padding: '0.6rem 1.2rem', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}>
            + Pridať novú firmu
          </button>
        </div>

        {/* Tabuľka firiem */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb' }}>
                <th style={th}>Logo</th>
                <th style={th}>Názov</th>
                <th style={th}>Slug</th>
                <th style={th}>Plán</th>
                <th style={th}>Integrácie</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Akcia</th>
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>Žiadne firmy</td></tr>
              ) : tenants.map(t => (
                <tr key={t.id} style={{ borderTop: '1px solid #e5e7eb', opacity: t.active ? 1 : 0.5 }}>
                  <td style={td}>
                    <div style={{ width: '60px', height: '40px', display: 'flex', alignItems: 'center' }}>
                      {t.logo_url ? <img src={`${BASE}${t.logo_url}`} alt="logo" style={{ maxWidth: '60px', maxHeight: '40px', objectFit: 'contain' }} /> : <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>}
                    </div>
                  </td>
                  <td style={{ ...td, fontWeight: 500 }}>{t.name}</td>
                  <td style={{ ...td, color: '#6b7280', fontFamily: 'monospace', fontSize: '0.85rem' }}>{t.slug}</td>
                  <td style={td}>
                    <span style={{ padding: '0.2rem 0.6rem', backgroundColor: '#eff6ff', color: '#1d4ed8', borderRadius: '999px', fontSize: '0.75rem', fontWeight: 500 }}>
                      {t.billing_plan}
                    </span>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                      {/* Integrácie — podľa reálnej konfigurácie (kľúče/host) */}
                      {t.imap_host && <span style={badge('#dcfce7', '#16a34a')}>✉ Email</span>}
                      {t.has_slack_bot_token && <span style={badge('#fef3c7', '#b45309')}>💬 Slack</span>}
                      {t.has_telegram && <span style={badge('#e0f2fe', '#0369a1')}>✈ Telegram</span>}
                      {/* Moduly — jednotne podľa feature flagov (tenants.features) */}
                      {t.features?.invoicing && <span style={badge('#dbeafe', '#1d4ed8')}>🧾 Fakturácia</span>}
                      {t.features?.orders && <span style={badge('#ffedd5', '#c2410c')}>📦 Objednávky</span>}
                      {t.features?.flowii_integration && <span style={badge('#e0e7ff', '#4338ca')}>🔗 FLOWii</span>}
                      {t.features?.omega_export && <span style={badge('#ede9fe', '#6d28d9')}>📊 Omega</span>}
                      {t.features?.resale_detection && <span style={badge('#cffafe', '#0e7490')}>🏷 Klasifikácia</span>}
                      {!t.accounting_email && <span style={badge('#fee2e2', '#dc2626')}>⚠ Bez schránky</span>}
                    </div>
                  </td>
                  <td style={td}>
                    {t.signup_status === 'pending' ? (
                      <span style={badge('#fef3c7', '#b45309')}>● Čaká na aktiváciu</span>
                    ) : t.active ? (
                      <span style={badge('#dcfce7', '#16a34a')}>● Aktívna</span>
                    ) : (
                      <span style={badge('#fee2e2', '#dc2626')}>● Deaktivovaná</span>
                    )}
                    {signupInterestLabel(t.signup_source) && (
                      <span style={{ ...badge('#dbeafe', '#1d4ed8'), marginLeft: '0.35rem' }}>{signupInterestLabel(t.signup_source)}</span>
                    )}
                  </td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                      {t.signup_status === 'pending' && (
                        <button onClick={() => handleApprove(t)}
                          style={{ ...btnPrimary, backgroundColor: '#16a34a' }}>
                          ✓ Aktivovať
                        </button>
                      )}
                      <button onClick={() => navigate(`/superadmin/tenant/${t.slug}`)}
                        style={btnPrimary}>
                        Editovať
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <UsagePanel />
      </div>

      {/* Modal: Pridať novú firmu */}
      {showCreate && (
        <div onClick={() => setShowCreate(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: '1rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', width: '100%', maxWidth: '500px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
            <h2 style={{ margin: '0 0 1rem', color: '#1f2937' }}>Pridať novú firmu</h2>
            <form onSubmit={handleCreate} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Vyhľadať firmu (názov alebo IČO)</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input
                    style={{ ...inputStyle, flex: 1 }}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); searchCompany(); } }}
                    placeholder="napr. WESTech, spol. s r.o.  alebo  35796111"
                  />
                  <button type="button" onClick={searchCompany}
                    disabled={searching || searchQuery.trim().length < 3}
                    style={{ ...btnNeutral, whiteSpace: 'nowrap', opacity: (searching || searchQuery.trim().length < 3) ? 0.6 : 1 }}>
                    {searching ? 'Hľadám…' : '🔍 Hľadať firmu'}
                  </button>
                </div>
                {searchResults && (
                  <div style={{ marginTop: '0.4rem', border: '1px solid #e5e7eb', borderRadius: 6, maxHeight: 200, overflowY: 'auto' }}>
                    {searchResults.length === 0 ? (
                      <p style={{ margin: 0, padding: '0.5rem 0.6rem', fontSize: '0.8rem', color: '#6b7280' }}>Žiadna firma nenájdená</p>
                    ) : searchResults.map((c, i) => (
                      <button key={c.ico || i} type="button" onClick={() => pickCandidate(c)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left', padding: '0.45rem 0.6rem',
                          border: 'none', borderBottom: i < searchResults.length - 1 ? '1px solid #f1f5f9' : 'none',
                          background: '#fff', cursor: 'pointer', fontSize: '0.82rem',
                        }}>
                        <strong>{c.name}</strong> <span style={{ color: '#6b7280' }}>· IČO {c.ico}</span>
                        <br />
                        <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                          {[c.address_street, c.address_zip, c.address_city].filter(Boolean).join(', ')}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
                <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#9ca3af' }}>
                  Zdroj: RPO (Štatistický úrad SR) — aktuálne dáta. Po výbere sa dotiahne DIČ (RÚZ) + overí IČ DPH (VIES).
                </p>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>IČO (voliteľné — manuálne predvyplnenie)</label>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <input style={{ ...inputStyle, flex: 1 }} value={lookupIco} onChange={e => setLookupIco(e.target.value.replace(/\s/g, ''))} placeholder="napr. 31322832" />
                  <button type="button" onClick={lookupFromRUZ} disabled={lookingUp || !lookupIco} style={{ ...btnNeutral, whiteSpace: 'nowrap', opacity: (lookingUp || !lookupIco) ? 0.6 : 1 }}>
                    {lookingUp ? 'Hľadám...' : '🔍 Načítať (RPO/RÚZ)'}
                  </button>
                </div>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Názov firmy</label>
                <input style={inputStyle} required value={newTenant.companyName} onChange={e => setNewTenant({...newTenant, companyName: e.target.value})} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Slug (URL identifikátor, napr. acme-corp)</label>
                <input style={inputStyle} required pattern="[a-z0-9-]+" value={newTenant.companySlug} onChange={e => setNewTenant({...newTenant, companySlug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})} />
              </div>
              <div>
                <label style={lbl}>Admin meno</label>
                <input style={inputStyle} required value={newTenant.adminFirstName} onChange={e => setNewTenant({...newTenant, adminFirstName: e.target.value})} />
              </div>
              <div>
                <label style={lbl}>Admin priezvisko</label>
                <input style={inputStyle} required value={newTenant.adminLastName} onChange={e => setNewTenant({...newTenant, adminLastName: e.target.value})} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Admin email</label>
                <input style={inputStyle} type="email" required value={newTenant.adminEmail} onChange={e => setNewTenant({...newTenant, adminEmail: e.target.value})} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={lbl}>Admin heslo</label>
                <input style={inputStyle} type="text" required value={newTenant.adminPassword} onChange={e => setNewTenant({...newTenant, adminPassword: e.target.value})} />
              </div>
              {createMessage && (
                <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: '0.85rem', color: createMessage.startsWith('✓') ? '#16a34a' : '#dc2626' }}>{createMessage}</p>
              )}
              <div style={{ gridColumn: '1 / -1', display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                <button type="button" onClick={() => setShowCreate(false)} style={btnNeutral}>Zrušiť</button>
                <button type="submit" disabled={creating} style={{ ...btnPrimary, padding: '0.5rem 1.5rem', opacity: creating ? 0.6 : 1 }}>
                  {creating ? 'Vytváram...' : 'Vytvoriť'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: '0.75rem 1rem', textAlign: 'left', color: '#6b7280', fontWeight: 500, fontSize: '0.85rem' };
const td: React.CSSProperties = { padding: '0.75rem 1rem', color: '#1f2937', fontSize: '0.9rem' };
const lbl: React.CSSProperties = { display: 'block', marginBottom: '0.25rem', color: '#374151', fontSize: '0.85rem' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '0.95rem', boxSizing: 'border-box' };
const btnPrimary: React.CSSProperties = { padding: '0.4rem 0.9rem', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };
const btnDanger: React.CSSProperties = { padding: '0.4rem 0.9rem', backgroundColor: '#fee2e2', color: '#dc2626', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };
const btnNeutral: React.CSSProperties = { padding: '0.4rem 0.9rem', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' };
const badge = (bg: string, color: string): React.CSSProperties => ({
  padding: '0.2rem 0.6rem', backgroundColor: bg, color, borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600, whiteSpace: 'nowrap'
});
